import React, { Component } from "react";
import Popup from "../Popup/Popup";
import Strings from "@my-dld/assets/strings";
import TextInputField from "../Form/TextInputField";
import NumberConverter from "../Form/Converters/NumberConverter";
import { CompanyActivity, RVSCompany, RVSCompanySearch } from "@eres/rvsapi/entities";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { DateUtil, LocaleUtil } from "@eres/core";
import { RVSApi } from "@eres/rvsapi";
import Pagination from "../Pagination";
import { RenderLoading } from "../RTRegistration/AddPartyPopup";
import { showMessage } from "@my-dld/utils/UIUtils";
import { formatAppDate } from "@my-dld/utils/Utils";

interface Props {
	complainTypeID: number;
	onClose: () => void;
	onCompanySelect: ( company: RVSCompany ) => void;
}

interface State {
	licenseNo?: string;
	orn?: string;
	companyNameEn?: string;
	companyNameAr?: string;
	errorMsg?: string;
	searchRequest?: RVSCompanySearch;
	searchResults?: RVSCompany[];
	totalRows: number;
	pageIndex: number;
	isLoading?: boolean;
	companySelError?: string;
}

const PAGE_SIZE = 10;

class CompanySearchPopup extends Component<Props, State> {
	constructor( props: Props ) {
		super( props );
		this.state = {
			totalRows: 0,
			pageIndex: 0,
		};
	}

	render() {
		const { errorMsg, searchResults, totalRows, isLoading, companySelError } = this.state;
		return <Popup
			autoOpenMount={ true }
			id={ "company_search_popup" }
			modalSubContainer={ "modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" }
			showIcon={ false }
			title={ Strings.search_company_license_number }
			onDismiss={ this.onClose }>
			<div className="row">
				<div className="col-md-12">
					{ errorMsg && <div className="alert alert-danger">{ errorMsg }</div> }
				</div>
				<TextInputField
					id={ "licenseNo" }
					holder={ this }
					label={ Strings.license_number }
					containerClassName={ "col-md-6 form-group" }
					type={ "text" }
					converter={ NumberConverter }/>
				<TextInputField
					id={ "orn" }
					holder={ this }
					label={ Strings.office_number }
					containerClassName={ "col-md-6 form-group" }
					type={ "text" }
					converter={ NumberConverter }/>
				<TextInputField
					id={ "companyNameEn" }
					holder={ this }
					label={ Strings.company_name_english }
					containerClassName={ "col-md-6 form-group" }
					type={ "english" }/>
				<TextInputField
					id={ "companyNameAr" }
					holder={ this }
					label={ Strings.company_name_arabic }
					containerClassName={ "col-md-6 form-group" }
					type={ "arabic" }/>
			</div>
			<div className="modal-footer justify-content-center">
				<button
					type="button" className="btn btn_1"
					onClick={ this.onSearch }>{ Strings.search_label }</button>
				<button
					type="button" className="btn btn-link"
					onClick={ this.onClose }>{ Strings.close_label }</button>
			</div>
			{ searchResults && <React.Fragment>
				<RenderCompanySearchResults
					companies={ searchResults }
					onCompanySelect={ this.onCompanySelect }
					error={ companySelError }/>
				<Pagination
					totalCount={ totalRows } pageSize={ PAGE_SIZE }
					onChangePage={ this.onChangePage }/>
			</React.Fragment> }
			{ isLoading && <RenderLoading/> }
		</Popup>;
	}

	private onSearch = () => {
		const companySearch = this.getCompanySearch();
		if ( companySearch ) {
			this.setState( {
				errorMsg: undefined,
				searchResults: undefined,
				searchRequest: companySearch,
				isLoading: true,
			}, () => {
				this.searchCompanies();
			} );
		} else {
			this.setState( { errorMsg: Strings.company_search_filter_required_msg } );
		}
	};

	private getCompanySearch(): RVSCompanySearch | undefined {
		const { licenseNo, orn, companyNameEn, companyNameAr } = this.state;
		if ( !isEmptyStringOrNull( licenseNo ) || !isEmptyStringOrNull( companyNameEn ) ||
			!isEmptyStringOrNull( companyNameAr ) || !isEmptyStringOrNull( orn ) ) {
			return {
				licenseNo: licenseNo,
				orn: orn,
				companyNameEn: companyNameEn,
				companyNameAr: companyNameAr,
			};
		}
		return undefined;
	}

	private onChangePage = ( pageIndex: number ) => {
		this.setState( {
			pageIndex: pageIndex,
			isLoading: true,
		}, () => {
			this.searchCompanies();
		} );
	};

	private searchCompanies() {
		const { pageIndex, searchRequest } = this.state;
		RVSApi.loadCompanies( pageIndex, PAGE_SIZE, searchRequest ).then( ( resp: any ) => {
			const results: RVSCompany[] = resp ? resp.AuthorityList : [];
			this.setState( {
				searchResults: results,
				totalRows: results.length > 0 ? results[ 0 ].TotalRowsCount : 0,
				isLoading: false,
				companySelError: undefined,
			} );
		} ).catch( reason => {
			this.setState( { isLoading: false } );
			showMessage( reason );
		} );
	}

	private onClose = () => {
		this.props.onClose();
	};

	private onCompanySelect = ( company: RVSCompany ) => {
		this.setState( { companySelError: undefined } );
		if ( IsCompanyHasSelComplainType( company, this.props.complainTypeID ) ) {
			this.props.onCompanySelect( company );
		} else {
			const companyName = LocaleUtil.getLocalValue( company );
			this.setState( {
				companySelError: Strings.rvs_company_complaint_type_mismatch.replace( "~", "\"" + companyName + "\"" ),
			} );
		}
	};
}

export function IsCompanyHasSelComplainType( company: RVSCompany, complainTypeID?: number ) {
	let index = -1;
	if ( complainTypeID && company.ActivitiesList && company.ActivitiesList.length > 0 ) {
		index = company.ActivitiesList.findIndex( ( activity: CompanyActivity ) => ( activity.ActivityId === complainTypeID ) );
	}
	return ( index !== -1 );
}

export function RenderCompanySearchResults( props: {
	companies: RVSCompany[], onCompanySelect: ( company: RVSCompany ) => void,
	error?: string,
} ) {
	const { companies, onCompanySelect, error } = props;
	return <div className="row rvs-search-company">
		<div className="col-md-12 content-section">
			<div className="content-section-title">
				<span className="section-title-span">{ Strings.results }</span>
			</div>
		</div>
		<div className="col-md-12">
			{ error && <div className="alert alert-danger">{ error }</div> }
			<div className="table-responsive">
				<table className="table table-striped table-bordered">
					<thead>
					<tr>
						<th>{ Strings.company_name_arabic }</th>
						<th>{ Strings.company_name_english }</th>
						<th>{ Strings.license_number }</th>
						<th>{ Strings.issue_date }</th>
						<th>{ Strings.expiry_date }</th>
					</tr>
					</thead>
					<tbody>
					{ companies.map( ( company: RVSCompany, index: number ) => {
						return <tr
							key={ "rvs_company_res_" + index }
							onClick={ () => onCompanySelect( company ) }
							style={ { cursor: "pointer" } }>
							<td data-label={ Strings.company_name_arabic }>{ company.NameAr }</td>
							<td data-label={ Strings.company_name_english }>{ company.NameEn }</td>
							<td data-label={ Strings.license_number }>{ company.ParticipantNumber }</td>
							<td data-label={ Strings.issue_date }>{ company.IssueDate ? formatAppDate( DateUtil.parseDate( company.IssueDate,
								"YYYY-MM-DDThh:mm:ss" ), "DD/MM/YYYY" ) : "-" }</td>
							<td data-label={ Strings.expiry_date }>{ company.ExpiryDate ? formatAppDate( DateUtil.parseDate( company.ExpiryDate,
								"YYYY-MM-DDThh:mm:ss" ), "DD/MM/YYYY" ) : "-" }</td>
						</tr>;
					} ) }
					</tbody>
				</table>
			</div>
		</div>
	</div>;
}

export default CompanySearchPopup;
